import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import { Segment } from 'entities/Segment/model/Segment'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { type IResponseFullSurvey } from 'entities/Surveys/api/types'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { SendFromStore } from 'widgets/SendFrom'

export class SurveyEnrollContactsStore {
  enrollmentFilters = EnrollmentFilters.Empty()
  enrollmentStore: EnrollmentStore
  numberStore: SendFromStore
  error = ''
  loading = false
  disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(readonly entities: IEntitiesStore) {
    this.numberStore = new SendFromStore(this.entities, {
      excludeDeprioritizedNumbers: true,
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
      withDynamicSender: this.entities.featureFlagsStore.send_as_contact_owner,
    })
    this.enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () =>
            ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'survey',
              skipValues: ['tag'],
            }).then(({ data }) => data),
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this.entities
    )

    this.initFilters()
    makeAutoObservable(this)

    this.reactionIsShowContactsAlert()
  }

  initFilters = () => this.enrollmentStore.filterEditorStore.initFilters()

  get paramsFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this.enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this.enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this.enrollmentFilters.tags?.map((tag) => tag.id),
      advanced: this.enrollmentFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  init = async ({ filters, send_from, settings }: IResponseFullSurvey) => {
    if (send_from) {
      void this.numberStore.setSendFrom(send_from)
    }

    if (filters) {
      try {
        this.loading = true
        const tagsIds = filters.tags ?? []
        const contactsIds = filters.contacts ?? []
        const segmentsIds = filters.segments ?? []

        const [tags, contacts] = await Promise.all([
          this.fetchTags(tagsIds),
          this.entities.contactsStore.getByIds(contactsIds),
        ])

        const segments = segmentsIds.reduce<Segment[]>((acc, id) => {
          const item = this.entities.segmentsStore.getItem(id)
          if (item) acc.push(item)
          return acc
        }, [])

        const advanced = filters.advanced?.map(
          (item) =>
            new Segment({
              id: -1,
              organization_id: 0,
              name: '',
              filters: item,
              is_favorite: false,
            })
        )

        runInAction(() => {
          this.enrollmentFilters.sync({ tags, contacts, segments, filters: advanced })
        })
      } finally {
        runInAction(() => {
          this.loading = false
        })
      }
    }

    const isReEnroll = settings?.allow_re_entering?.value ?? false
    this.enrollmentStore.setIsContactReEnroll(isReEnroll)
    this.enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
  }

  setError = (value: string) => {
    this.error = value
  }

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()

    this.enrollmentStore.openModal({
      filters,
      action: {
        cancel: () => Promise.resolve(true),
        confirm: async (newFilters: EnrollmentFilters) => {
          runInAction(() => {
            this.enrollmentFilters = newFilters
          })
          return true
        },
      },
      allowEmptyCount: true,
      title: 'Contacts',
    })
  }

  fetchTags = async (ids: number[]) => {
    const tags = await TagsApi.getTagsById(ids)

    return tags.map((tag) => new Tag(tag))
  }

  dispose = () => {
    this.disposeIsShowContactsAlert?.()
  }

  reactionIsShowContactsAlert = () => {
    this.disposeIsShowContactsAlert = reaction(
      () => [this.enrollmentStore.isContactReEnroll, this.enrollmentFilters.filters],
      () => this.enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    )
  }
}
